import React, {useMemo} from 'react';
import cn from 'classnames';
import {Speaker, TopicMatch, TranscriptBlock} from '@store/call-card/CallCard.types';
import {List, Map} from 'immutable';
import {Color, Seconds} from '@store/@types/util';
import {NarrowTranscriptLine} from '@components/call-card/NarrowTranscriptLine';
import {associateBy} from 'utils/associateBy';
import './NarrowTranscript.sass';
import {TopicId} from '@store/topic-keyword/TopicKeyword.types';
import {combineMatchesByLine} from '@components/call-card/TranscriptMatch.utils';
import {FileUploadState, inPreTranscribedState, inErrorState} from 'records/FileUploadState';

interface NarrowTranscriptProps {
  transcript: List<TranscriptBlock>;
  speakers: List<Speaker>;
  // Should the sidebar be collapsed or shown
  hidden: boolean;
  matches: Map<TopicId, List<TopicMatch>>;
  activeTopics: Map<TopicId, Color>;
  setTimeSeconds: (seconds: Seconds) => void;
  callState: FileUploadState;
  currentTime: Seconds;
  setTranscriptRef: (ref: HTMLElement | null) => void;
}

export const NarrowTranscript: React.FC<NarrowTranscriptProps> = ({
  speakers,
  transcript,
  hidden,
  matches,
  activeTopics,
  setTimeSeconds,
  callState,
  currentTime,
  setTranscriptRef,
}) => {
  const speakerMap = useMemo(() => associateBy(speakers, s => s.speakerNumber), [speakers]);
  const matchedBlocks = useMemo(
    () => combineMatchesByLine(matches, activeTopics),
    [matches, activeTopics]
  );

  if (matchedBlocks.isEmpty()) {
    return (
      <div className={cn('narrow-transcript', {hidden})} ref={ref => setTranscriptRef(ref)}>
        {transcript.map(b => (
          <NarrowTranscriptLine
            key={b.startTime}
            onClick={() => setTimeSeconds(b.startTime)}
            chunk={b.chunk}
            speaker={speakerMap.get(b.speakerNumber)!}
            startTime={b.startTime}
            activeTopics={activeTopics}
            matches={List()}
            highlightLine={currentTime >= b.startTime && currentTime < b.endTime}
          />
        ))}
        {transcript.isEmpty() && inPreTranscribedState(callState) && (
          <div className="transcript-state">
            <p>We are processing this transcript.</p>
            <p>Check back shortly.</p>
          </div>
        )}
        {transcript.isEmpty() && inErrorState(callState) && (
          <div className="transcript-state">
            <p>We cannot process this transcript.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('narrow-transcript', {hidden})}>
      {matchedBlocks
        .map((matches, startTime) => {
          const firstMatch = matches.first();
          return (
            <NarrowTranscriptLine
              key={startTime}
              onClick={() => setTimeSeconds(firstMatch.startTime)}
              chunk={firstMatch.chunk}
              speaker={speakerMap.get(firstMatch.speakerNumber)!}
              startTime={startTime}
              activeTopics={activeTopics}
              matches={matches}
              highlightLine={
                currentTime >= firstMatch.startTime && currentTime < firstMatch.endTime
              }
            />
          );
        })
        .toList()}
    </div>
  );
};
